// TFVF Retraining — trainee sees their active plans; manager creates
// new plans, browses auto-detected triggers, attaches next-3-call
// reviews, and updates statuses.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, RotateCcw, CheckCircle2, XCircle, Clock,
  ChevronRight, Plus, Zap, TrendingUp, TrendingDown,
} from 'lucide-react';
import { API, authHeaders } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const LEVEL_META = {
  coaching_nudge:    { label: 'Coaching Nudge',    tone: 'bg-sky-100 text-sky-800' },
  micro_retraining:  { label: 'Micro Retraining',  tone: 'bg-indigo-100 text-indigo-800' },
  focused_practice:  { label: 'Focused Practice',  tone: 'bg-amber-100 text-amber-800' },
  formal_recert:     { label: 'Formal Recertification', tone: 'bg-rose-100 text-rose-800' },
};
const STATUS_META = {
  active:       { label: 'Active',        tone: 'bg-indigo-100 text-indigo-800' },
  improved:     { label: 'Improved',      tone: 'bg-emerald-100 text-emerald-800' },
  partial:      { label: 'Partial',       tone: 'bg-amber-100 text-amber-800' },
  not_improved: { label: 'Not Improved',  tone: 'bg-rose-100 text-rose-800' },
  continue:     { label: 'Continue',      tone: 'bg-sky-100 text-sky-800' },
  completed:    { label: 'Completed',     tone: 'bg-slate-200 text-slate-700' },
};

const TfvfRetraining = () => {
  const { token, user } = useAuth();
  const isManager = user && ['master_admin','admin','domain_admin','office_manager','domain_supervisor','supervisor','manager'].includes(user.role);
  const [myPlans, setMyPlans] = useState([]);
  const [team, setTeam] = useState([]);
  const [teamPlans, setTeamPlans] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ user_id: '', level: 'micro_retraining', criterion: '', note: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [axios.get(`${API}/tfvf/retraining/mine`, { headers: authHeaders(token) })];
      if (isManager) {
        promises.push(axios.get(`${API}/tfvf/manager/dashboard`, { headers: authHeaders(token) }));
        promises.push(axios.get(`${API}/tfvf/retraining/triggers`, { headers: authHeaders(token) }).catch(() => ({ data: { triggers: [] } })));
      }
      const [mine, dash, trig] = await Promise.all(promises);
      setMyPlans(mine.data?.plans || []);
      if (dash) {
        const rows = dash.data?.rows || [];
        setTeam(rows);
        // Fetch team plans in parallel
        const perUser = await Promise.all(rows.map(r =>
          axios.get(`${API}/tfvf/retraining/user/${r.user_id}`, { headers: authHeaders(token) })
            .then(res => res.data?.plans || []).catch(() => []),
        ));
        setTeamPlans(perUser.flat());
      }
      if (trig) setTriggers(trig.data?.triggers || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [token, isManager]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const createPlan = async () => {
    if (!newForm.user_id || !newForm.criterion) {
      toast.info('Staff and criterion are required'); return;
    }
    try {
      await axios.post(`${API}/tfvf/retraining/assign`, newForm, { headers: authHeaders(token) });
      toast.success('Retraining plan created');
      setShowNew(false); setNewForm({ user_id: '', level: 'micro_retraining', criterion: '', note: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assign failed');
    }
  };

  const acceptTrigger = async (t) => {
    if (!t.user_id) { toast.warning('Trigger has no linked user — assign manually'); return; }
    try {
      await axios.post(`${API}/tfvf/retraining/assign`, {
        user_id: t.user_id,
        level: t.suggested_level,
        criterion: t.criterion,
        source_call_ids: t.call_ids,
        trigger_ref: t.id,
        note: `Auto-trigger: ${t.kind}`,
      }, { headers: authHeaders(token) });
      toast.success(`${LEVEL_META[t.suggested_level].label} assigned to ${t.user_name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const updateStatus = async (planId, status) => {
    try {
      await axios.patch(`${API}/tfvf/retraining/${planId}/status`, { status }, { headers: authHeaders(token) });
      toast.success(`Marked ${STATUS_META[status].label}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;

  return (
    <div className="space-y-5" data-testid="tfvf-retraining-page">
      <div className="tf-card rounded-xl p-5">
        <p className="tf-eyebrow">Retraining</p>
        <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">Behaviour-specific coaching & recertification</h2>
        <p className="text-xs text-[color:var(--tf-muted)] mt-1">
          Four levels — Coaching Nudge → Micro Retraining → Focused Practice → Formal Recertification.
          Each plan tracks one behaviour across the next three relevant calls.
        </p>
      </div>

      {/* My active plans */}
      {myPlans.length > 0 && (
        <section className="tf-card rounded-xl p-5" data-testid="tfvf-my-retraining">
          <p className="tf-eyebrow mb-3">My plans ({myPlans.length})</p>
          <div className="space-y-2">
            {myPlans.map(p => (
              <div key={p.id} className="border border-[color:var(--tf-forest)]/10 rounded-lg p-3" data-testid={`retraining-mine-${p.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${LEVEL_META[p.level]?.tone}`}>{LEVEL_META[p.level]?.label}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_META[p.status]?.tone}`}>{STATUS_META[p.status]?.label}</span>
                  <span className="text-sm font-semibold text-[color:var(--tf-forest)]">{p.criterion}</span>
                  <span className="text-xs text-[color:var(--tf-muted)]">by {p.manager_name}</span>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  {p.approved_words?.length > 0 && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                      <p className="text-[10px] font-bold text-emerald-800 uppercase">Approved words</p>
                      <p className="text-emerald-900 italic">{p.approved_words.join(' · ')}</p>
                    </div>
                  )}
                  {p.words_to_avoid?.length > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded p-2">
                      <p className="text-[10px] font-bold text-rose-800 uppercase">Words to avoid</p>
                      <p className="text-rose-900">{p.words_to_avoid.join(' · ')}</p>
                    </div>
                  )}
                  <div className="bg-[color:var(--tf-parchment)] border border-[color:var(--tf-forest)]/10 rounded p-2">
                    <p className="text-[10px] font-bold text-[color:var(--tf-forest)] uppercase">Next 3 calls</p>
                    <p className="text-[color:var(--tf-slate)]">{p.reviews_count || 0} / 3 reviewed · {p.demonstrated_count || 0} demonstrated</p>
                  </div>
                </div>
                {p.note && <p className="text-xs text-[color:var(--tf-muted)] italic mt-2">Manager note: {p.note}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Manager: auto-triggers */}
      {isManager && triggers.length > 0 && (
        <section className="tf-card rounded-xl p-5" data-testid="tfvf-triggers">
          <p className="tf-eyebrow flex items-center gap-2"><Zap className="w-3 h-3" /> Auto-detected retraining triggers ({triggers.length})</p>
          <div className="mt-2 space-y-2">
            {triggers.map(t => (
              <div key={t.id} className={`rounded-lg p-3 border ${t.hard ? 'border-rose-300 bg-rose-50' : 'border-amber-300 bg-amber-50'}`} data-testid={`retraining-trigger-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.hard
                        ? <AlertTriangle className="w-4 h-4 text-rose-600" />
                        : <TrendingDown className="w-4 h-4 text-amber-600" />}
                      <span className="text-sm font-semibold text-[color:var(--tf-forest)]">{t.user_name}</span>
                      <span className="text-xs text-[color:var(--tf-muted)]">· {t.criterion}</span>
                    </div>
                    <p className="text-xs text-[color:var(--tf-slate)] mt-1">
                      {t.kind === 'criterion_missed_3_of_5'
                        ? <>Missed {t.missed} of {t.total} recent calls ({t.miss_pct}%) — suggests <b>{LEVEL_META[t.suggested_level]?.label}</b></>
                        : <>Governance failure on call {t.call_ids?.[0]?.slice(0,10)} — requires <b>{LEVEL_META[t.suggested_level]?.label}</b></>}
                    </p>
                  </div>
                  <button
                    onClick={() => acceptTrigger(t)}
                    className="tf-btn-primary rounded-lg px-3 py-1.5 text-xs font-semibold shrink-0"
                    data-testid={`retraining-accept-${t.id}`}
                  >
                    Assign
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Manager: team plans + create */}
      {isManager && (
        <section className="tf-card rounded-xl p-5" data-testid="tfvf-team-retraining">
          <div className="flex items-center justify-between mb-3">
            <p className="tf-eyebrow">Team retraining plans ({teamPlans.length})</p>
            <button onClick={() => setShowNew(v => !v)} className="tf-btn-gold rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1" data-testid="retraining-new-btn">
              <Plus className="w-3 h-3" /> New plan
            </button>
          </div>

          {showNew && (
            <div className="border border-[color:var(--tf-gold)]/40 bg-[color:var(--tf-parchment)]/40 rounded-lg p-3 mb-4 space-y-2" data-testid="retraining-new-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <select value={newForm.user_id} onChange={e => setNewForm(f => ({ ...f, user_id: e.target.value }))} className="border border-[color:var(--tf-forest)]/15 rounded px-2 py-1.5 text-sm bg-white">
                  <option value="">Pick staff</option>
                  {team.map(r => <option key={r.user_id} value={r.user_id}>{r.name || r.email}</option>)}
                </select>
                <select value={newForm.level} onChange={e => setNewForm(f => ({ ...f, level: e.target.value }))} className="border border-[color:var(--tf-forest)]/15 rounded px-2 py-1.5 text-sm bg-white">
                  {Object.entries(LEVEL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <input value={newForm.criterion} onChange={e => setNewForm(f => ({ ...f, criterion: e.target.value }))} placeholder="Criterion (e.g. green_brain, rule_of_three, close)" className="w-full border border-[color:var(--tf-forest)]/15 rounded px-2 py-1.5 text-sm bg-white" />
              <textarea value={newForm.note} onChange={e => setNewForm(f => ({ ...f, note: e.target.value }))} rows={2} placeholder="Note (why this focus, source calls, expected wording)" className="w-full border border-[color:var(--tf-forest)]/15 rounded px-2 py-1.5 text-sm bg-white" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowNew(false)} className="text-xs text-[color:var(--tf-muted)] px-3 py-1.5">Cancel</button>
                <button onClick={createPlan} className="tf-btn-primary rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="retraining-new-submit">Create plan</button>
              </div>
            </div>
          )}

          {teamPlans.length === 0 ? (
            <p className="text-center text-sm text-[color:var(--tf-muted)] italic py-4">No plans yet.</p>
          ) : (
            <div className="divide-y divide-[color:var(--tf-forest)]/10">
              {teamPlans.map(p => (
                <div key={p.id} className="py-3" data-testid={`retraining-team-${p.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[color:var(--tf-forest)]">{p.user_name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${LEVEL_META[p.level]?.tone}`}>{LEVEL_META[p.level]?.label}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_META[p.status]?.tone}`}>{STATUS_META[p.status]?.label}</span>
                    <span className="text-xs text-[color:var(--tf-muted)]">· {p.criterion}</span>
                  </div>
                  <p className="text-xs text-[color:var(--tf-muted)] mt-1">
                    Assigned {new Date(p.assigned_at).toLocaleDateString()} ·
                    {' '}{p.reviews_count || 0}/3 calls reviewed
                    {p.suggested_result && <> · <b>Auto-suggest:</b> {p.suggested_result}</>}
                  </p>
                  {p.status === 'active' && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <button onClick={() => updateStatus(p.id, 'improved')} className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded px-2 py-1 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Improved</button>
                      <button onClick={() => updateStatus(p.id, 'partial')} className="text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Partial</button>
                      <button onClick={() => updateStatus(p.id, 'not_improved')} className="text-xs bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 rounded px-2 py-1 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Not improved</button>
                      <button onClick={() => updateStatus(p.id, 'continue')} className="text-xs bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 rounded px-2 py-1 inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Continue</button>
                      <button onClick={() => updateStatus(p.id, 'completed')} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded px-2 py-1 ml-auto">Complete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default TfvfRetraining;
